import { Component, ElementRef, EventEmitter, inject, OnDestroy, OnInit, Output, signal, viewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { ChecklistFile, ChecklistItem } from './new-introducer-checklist-interfaces/new-introducer-checklist.interface';
import { SessionStorageService } from '../../../core/services/session-storage/session-storage.service';
import { SnackbarService } from '../../../shared/services/snackbar/snackbar.service';
import { Router } from '@angular/router';
import { NewIntroducerChecklistService } from './new-introducer-checklist-services/new-introducer-checklist.service';
import { NewRepChecklistModel } from '../new-rep-checklist/new-rep-checklist-models/new-rep-checklist.model';
import { HttpStatusCode } from 'axios';
import { NewRepChecklist } from '../new-rep-checklist/new-rep-checklist-interfaces/new-rep-checklist.interface';
import { getChecklistItems } from '../new-rep-checklist/new-rep-checklist-interfaces/save-new-rep-checklist.interface';

@Component({
  selector: 'app-new-introducer-checklist',
  imports: [
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIcon,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltipModule,
    CommonModule
  ],
  templateUrl: './new-introducer-checklist.html',
  styleUrl: './new-introducer-checklist.scss',
  host: {
    '(document:keydown.escape)': 'onEscape()',
    '(document:fullscreenchange)': 'syncFullscreenState()',
  }
})
export class NewIntroducerChecklist implements OnInit, OnDestroy {
  @Output() next = new EventEmitter();

  private sanitizer = inject(DomSanitizer);
  private fb = inject(FormBuilder);
  private sessionStorageService = inject(SessionStorageService);
  private snackBarService = inject(SnackbarService);
  private router = inject(Router);
  private newintroducerChecklistService = inject(NewIntroducerChecklistService);
  setSprinner = signal(false);
  protected currentUserRole: string = "";

  data = history.state;
  viewId!: number;
  isReadOnly = signal(false);
  iseditable = signal(false);
  designation!: string;
  readonly message = 'This field is required.';
  protected viewfiles: NewRepChecklist[] = [];
  protected getchecklistItems: getChecklistItems[] = [];


  // ---- Attachment checklist (RM assessment) ----
  protected checklistItems!: ChecklistItem[];
  role!: string;

  assessmentForm: FormGroup = this.fb.group({
    name: [''],
    manager: [''],
    branch: [''],
    checklist: this.fb.array([]) // 🔥 dynamic
  });


  private readonly previewPanel = viewChild<ElementRef<HTMLElement>>('previewPanel');

  selectedDocument: any = '';
  isPdf = false;
  selectedItemFiles: ChecklistFile[] = [];
  selectedFileIndex = 0;

  /** Preview panel state. */
  showPreview = false;
  isFullscreen = false;
  previewLoading = false;
  previewError = '';

  /** Links the open document back to its checklist row. */
  activePreviewId: number | null = null;
  activePreviewTitle = '';

  private objectUrl: string | null = null;

  readonly MIN_ZOOM = 0.5;
  readonly MAX_ZOOM = 3;
  private readonly ZOOM_STEP = 0.25;
  zoomLevel = 1;

  isPanning = false;
  private panOrigin: { x: number; y: number; left: number; top: number } | null = null;

  /** Opens (or retargets) the side-by-side preview for a checklist row. */
  previewDoc(item: ChecklistItem): void {
    if (!item?.files?.length) {
      return;
    }

    this.activePreviewId = item.id;
    this.activePreviewTitle = item.title;

    this.selectedItemFiles = item.files;
    this.showPreview = true;
    void this.loadFile(0);
  }

  async loadFile(index: number): Promise<void> {
    const file = this.selectedItemFiles?.[index];
    if (!file) {
      return;
    }

    this.selectedFileIndex = index;
    this.resetZoom();
    this.isPdf = !!file.name && file.name.toLowerCase().endsWith('.pdf');

    if (!file.name) {
      this.previewError = 'This file has no name and cannot be previewed.';
      return;
    }

    this.previewError = '';
    this.previewLoading = true;
    this.releaseObjectUrl();
    this.selectedDocument = '';

    try {
      const blob = await this.newintroducerChecklistService.viewChecklistdocuments(file.fileid);

      // Guard against a slow response landing after the user switched files.
      if (this.selectedFileIndex !== index) {
        return;
      }

      this.objectUrl = URL.createObjectURL(blob);
      this.selectedDocument = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
    } catch (error) {
      console.error(error);
      if (this.selectedFileIndex === index) {
        this.previewError = 'We couldn’t load this document. Please try again.';
      }
    } finally {
      if (this.selectedFileIndex === index) {
        this.previewLoading = false;
      }
    }
  }

  get zoomPercent(): number {
    return Math.round(this.zoomLevel * 100);
  }

  zoomIn(): void {
    this.zoomLevel = Math.min(this.MAX_ZOOM, +(this.zoomLevel + this.ZOOM_STEP).toFixed(2));
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(this.MIN_ZOOM, +(this.zoomLevel - this.ZOOM_STEP).toFixed(2));
  }

  resetZoom(): void {
    this.zoomLevel = 1;
  }

  onWheelZoom(event: WheelEvent): void {
    if (this.isPdf || !event.ctrlKey) {
      return;
    }

    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  /** Drag-to-pan a zoomed-in document; the scroll container does the work. */
  onPanStart(event: PointerEvent, scroller: HTMLElement): void {
    if (this.isPdf || this.zoomLevel <= 1 || event.button !== 0) {
      return;
    }

    this.panOrigin = {
      x: event.clientX,
      y: event.clientY,
      left: scroller.scrollLeft,
      top: scroller.scrollTop,
    };
    this.isPanning = true;
    scroller.setPointerCapture(event.pointerId);
  }

  onPanMove(event: PointerEvent, scroller: HTMLElement): void {
    if (!this.panOrigin) {
      return;
    }

    scroller.scrollLeft = this.panOrigin.left - (event.clientX - this.panOrigin.x);
    scroller.scrollTop = this.panOrigin.top - (event.clientY - this.panOrigin.y);
  }

  onPanEnd(event: PointerEvent, scroller: HTMLElement): void {
    if (!this.panOrigin) {
      return;
    }

    this.panOrigin = null;
    this.isPanning = false;
    if (scroller.hasPointerCapture(event.pointerId)) {
      scroller.releasePointerCapture(event.pointerId);
    }
  }

  async toggleFullscreen(): Promise<void> {
    const panel = this.previewPanel()?.nativeElement;
    if (!panel) {
      return;
    }

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await panel.requestFullscreen();
      }
    } catch (error) {
      console.error(error);
    }
  }

  syncFullscreenState(): void {
    this.isFullscreen = !!this.previewPanel()
      && document.fullscreenElement === this.previewPanel()!.nativeElement;
  }

  onEscape(): void {
    // In full screen the browser handles Escape itself — don't also close the panel.
    if (this.isFullscreen || !this.showPreview) {
      return;
    }

    this.closePreview();
  }

  closePreview(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch((error) => console.error(error));
    }

    this.showPreview = false;
    this.selectedDocument = '';
    this.selectedItemFiles = [];
    this.selectedFileIndex = 0;
    this.activePreviewId = null;
    this.activePreviewTitle = '';
    this.previewError = '';
    this.previewLoading = false;
    this.isPanning = false;
    this.panOrigin = null;
    this.releaseObjectUrl();
    this.resetZoom();
  }

  private releaseObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  ngOnDestroy(): void {
    this.releaseObjectUrl();
  }

  ngOnInit(): void {

    this.currentUserRole = this.sessionStorageService.getCurrentUserRole();
    this.designation = this.data.designation;
    if (this.designation == 'Introducer - Individual') {
      this.role = 'INDIVIDUAL';
      this.checklistItems = [
        { id: 1, title: 'Certified True Copy NRIC or Passport & Proof of Address ', files: [] },
        { id: 2, title: 'Bank Account Statement (within last 3 months)', files: [] },
        {
          id: 3,
          title: 'Proof of employment (e.g. Latest 6 months CPF Contributions statement or latest 3 months payslips, or latest 1 month employment acknowledgment letter (signed & stamped) from employer)',
          files: []
        },
      ];

    } else {
      this.role = 'CORPORATE';
      this.checklistItems = [
        { id: 1, title: 'Authorised Director and liaison personnel’s (Certified True Copy) NRIC or Passport & Proof of Address', files: [] },
        { id: 2, title: 'Company’s Bank Account Details (within last 3 months)', files: [] },
        {
          id: 3,
          title: 'ACRA BizFile (within last 3 months)',
          files: []
        },
        { id: 4, title: 'NRIC/Passport of the Beneficial Owner(s).', files: [] },
      ];
    }
    this.initChecklist();
    this.viewId = this.data.id;
    this.isReadOnly.set(this.data.isReadOnly);
    this.iseditable.set(this.data.iseditable);
    if ((this.currentUserRole == 'DSU' || this.currentUserRole == 'DSU Manager') && this.iseditable()) {
      this.isReadOnly.set(false);
    }
    if (this.isReadOnly() == true) {
      this.assessmentForm?.disable();
    } else {
      this.assessmentForm?.enable();
    }
    this.viewChecklistDocuments();
    this.getChecklistItems();

  }

  flatChecklist: any[] = [];

  private initChecklist() {

    this.flatChecklist = [];

    this.checklistItems.forEach(section => {
      this.flatChecklist.push({
        section
      });
    });

    const checklistArray = this.flatChecklist.map((item, index) =>
      this.fb.group({
        rmCheck: [false, [Validators.requiredTrue]]
      })
    );
    this.assessmentForm.setControl(
      'checklist',
      this.fb.array(checklistArray)
    );
  }

  // ✅ Getter
  get checklistForm(): FormArray {
    return this.assessmentForm.get('checklist') as FormArray;
  }

  onCheckboxChange(checked: boolean, index: number) {

    const checkbox = this.assessmentForm.get('checklist') as FormArray;

    if (checked) {
      checkbox.at(index)?.get('rmCheck')?.patchValue(true);
      checkbox.at(index)?.get('rmCheck')?.markAsUntouched();
    } else {
      checkbox?.at(index)?.get('rmCheck')?.patchValue(false);
      checkbox?.at(index)?.get('rmCheck')?.markAsTouched();
    }
  }

  async viewChecklistDocuments() {
    this.setSprinner.set(true);
    const responseData = await this.newintroducerChecklistService
      .viewChecklistdocumentslist(this.viewId, this.designation)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);

    if (responseData.status === HttpStatusCode.Ok) {

      // console.log(responseData);
      this.viewfiles = responseData.data.responsedata;
      // console.log(this.viewfiles);

      this.checklistItems.forEach((item: ChecklistItem) => {

        // Match based on item index / id / title
        if (item.id === 1) {

          const files = this.viewfiles
            .filter((file: any) =>
              file.onboardingDocumentType === 'Attachment (NRIC / Passport)' ||
              file.onboardingDocumentType === 'Attachment (Address Proof)'
            )
            .map((file: any) => ({
              fileid: file.id,
              name: file.fileName,
              url: file.filePath
            }));

          item.files = [...(item.files || []), ...files];
        }

        if (item.id === 2) {

          const files = this.viewfiles
            .filter((file: any) =>
              file.onboardingDocumentType === 'Attachment (Bank Account Statement within last 3 months)'
            )
            .map((file: any) => ({
              fileid: file.id,
              name: file.fileName,
              url: file.filePath
            }));

          item.files = [...(item.files || []), ...files];
        }

        if (item.id === 3) {

          const files = this.viewfiles
            .filter((file: any) =>
              file.onboardingDocumentType === 'Attachment (Proof of employment)' ||
              file.onboardingDocumentType === 'ACRA BizFile (within last 3 months)'
            )
            .map((file: any) => ({
              fileid: file.id,
              name: file.fileName,
              url: file.filePath
            }));

          item.files = [...(item.files || []), ...files];
        }

        if (this.designation !== 'Introducer - Individual') {
          if (item.id === 4) {

            const files = this.viewfiles
              .filter((file: any) =>
                file.onboardingDocumentType === 'NRIC/Passport of the Beneficial Owner(s)'
              )
              .map((file: any) => ({
                fileid: file.id,
                name: file.fileName,
                url: file.filePath
              }));

            item.files = [...(item.files || []), ...files];
          }

        }
      });

      this.assessmentForm.controls['name']?.setValue(this.viewfiles[0]?.name);
      this.assessmentForm.controls['manager']?.setValue(this.viewfiles[0]?.manager);
      this.assessmentForm.controls['branch']?.setValue(this.viewfiles[0]?.branch);

    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  async getChecklistItems() {
    this.setSprinner.set(true);
    const responseData = await this.newintroducerChecklistService
      .getChecklistItems(this.viewId)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);

    if (responseData.status === HttpStatusCode.Ok) {

      this.getchecklistItems = responseData.data.responsedata;

      this.getchecklistItems.forEach((item, index) => {
        this.checklistForm.at(index)?.patchValue({
          rmCheck: this.getchecklistItems[index]?.isCompleted,
        })
      });

    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  // ✅ Submit
  async saveAndContinue() {
    this.setSprinner.set(true);
    let saveNewRepchecklist = new NewRepChecklistModel();
    saveNewRepchecklist.setonboarding_id(this.viewId);
    const newrepChecklist = this.checklistForm.controls.map((control: any, index: number) => ({

      id: this.getchecklistItems[index]?.id,
      question_id: 0,
      category_name: this.designation == 'Introducer - Individual' ? 'iiDocumentCheck_' + (index + 1) : 'icDocumentCheck_' + (index + 1),
      is_completed: control.get('rmCheck').value,
      response_value: '',
      onboarding_id: this.viewId.toString(),
      updated_by_role: this.sessionStorageService.getCurrentUserRole(),
    }));
    saveNewRepchecklist.setChecklist([...newrepChecklist]);
    const responseData = await this.newintroducerChecklistService
      .savenewRepChecklistItems(saveNewRepchecklist)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);
    if (responseData.status === HttpStatusCode.Ok) {
      this.snackBarService.openSuccessSnackBar("Application saved successfully!");
      this.getChecklistItems();
      this.next.emit();
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }



}
